import { EntityType, ENTITY_FIELDS } from '@/hooks/useImport';
import { cn } from '@/lib/utils';

interface ImportPreviewProps {
  previewData: Record<string, string>[];
  entityType: EntityType;
  totalRows: number;
  validCount: number;
  invalidCount: number;
  mapping: Record<string, string>;
}

export function ImportPreview({
  previewData,
  entityType,
  totalRows,
  validCount,
  invalidCount,
}: ImportPreviewProps) {
  const fields = ENTITY_FIELDS[entityType];
  // Only show columns that have mappings
  const mappedFields = fields.filter(
    f =>
      Object.values(previewData[0] || {}).length > 0 &&
      previewData.some(row => row[f.key] !== undefined)
  );

  // Fallback: if no preview data, show all fields that are mapped
  const displayFields = mappedFields.length > 0 ? mappedFields : fields;

  return (
    <div className="space-y-4">
      {/* Validation Summary */}
      <div className="space-y-2">
        {validCount > 0 && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200">
            <svg
              className="w-4 h-4 text-green-600 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <p className="text-sm text-green-700">
              <span className="font-medium">{validCount}</span> rows ready to import
            </p>
          </div>
        )}

        {invalidCount > 0 && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
            <svg
              className="w-4 h-4 text-amber-600 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <p className="text-sm text-amber-700">
              <span className="font-medium">{invalidCount}</span> rows with missing required fields
              will be skipped
            </p>
          </div>
        )}

        {validCount === 0 && invalidCount > 0 && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
            <svg
              className="w-4 h-4 text-destructive shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            <p className="text-sm text-destructive">
              No valid rows to import. Please check your field mapping.
            </p>
          </div>
        )}
      </div>

      {/* Preview Table */}
      {previewData.length > 0 && (
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground w-10">
                    #
                  </th>
                  {displayFields.map(field => (
                    <th
                      key={field.key}
                      className="px-3 py-2 text-left text-xs font-medium text-muted-foreground"
                    >
                      {field.label}
                      {field.required && <span className="text-amber-600 ml-1">*</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {previewData.map((row, index) => (
                  <tr key={index} className="hover:bg-muted/30">
                    <td className="px-3 py-2 text-xs text-muted-foreground">{index + 1}</td>
                    {displayFields.map(field => {
                      const value = row[field.key];
                      const isEmpty = !value?.trim();
                      const isRequired = field.required;

                      return (
                        <td
                          key={field.key}
                          className={cn(
                            'px-3 py-2 text-foreground',
                            isEmpty && isRequired && 'bg-amber-50/50'
                          )}
                        >
                          {value || (
                            <span
                              className={cn(
                                'text-xs italic',
                                isRequired ? 'text-amber-600' : 'text-muted-foreground'
                              )}
                            >
                              {isRequired ? 'Missing' : '-'}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Row count indicator */}
          <div className="px-3 py-2 bg-muted/30 border-t border-border">
            <p className="text-xs text-muted-foreground">
              Showing {previewData.length} of {totalRows} rows
            </p>
          </div>
        </div>
      )}

      {/* Empty state */}
      {previewData.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <p className="text-sm">No preview data available</p>
        </div>
      )}
    </div>
  );
}
